// Client-side seed encryption (AES-GCM). The key lives server-side (released
// after reauth); only ciphertext is ever stored locally / in CloudStorage.

const PREFIX = 'enc:v1:';

const toB64 = (buf: ArrayBuffer | Uint8Array) =>
  btoa(String.fromCharCode(...new Uint8Array(buf as ArrayBuffer)));
const fromB64 = (s: string) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

/** Generate a fresh 256-bit AES-GCM key, exported as base64. */
export async function generateKeyB64(): Promise<string> {
  const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, [
    'encrypt',
    'decrypt',
  ]);
  return toB64(await crypto.subtle.exportKey('raw', key));
}

async function importKey(keyB64: string) {
  return crypto.subtle.importKey('raw', fromB64(keyB64), { name: 'AES-GCM' }, false, [
    'encrypt',
    'decrypt',
  ]);
}

/** Whether a stored value is our ciphertext (vs a legacy plaintext mnemonic). */
export const isEncrypted = (v: string) => v.startsWith(PREFIX);

export async function encryptSeed(mnemonic: string, keyB64: string): Promise<string> {
  const key = await importKey(keyB64);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    new TextEncoder().encode(mnemonic),
  );
  const combined = new Uint8Array(iv.length + ct.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(ct), iv.length);
  return PREFIX + toB64(combined);
}

export async function decryptSeed(ciphertext: string, keyB64: string): Promise<string> {
  const key = await importKey(keyB64);
  const combined = fromB64(ciphertext.slice(PREFIX.length));
  const iv = combined.subarray(0, 12);
  const ct = combined.subarray(12);
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ct);
  return new TextDecoder().decode(pt);
}
